import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ModalInfo, EventDetails } from 'shared/components';
import PencilIcon from 'assets/svgs/pencilIcon.svg';
import GarbageIcon from 'assets/svgs/garbageIcon.svg';
import { isEmpty } from 'lodash';

import { getRoomTimingName } from 'shared/helpers';
import './RoomDetailsModal.scss';

const mapFacilities = (facilities, roomOptions) =>
  facilities.map((facility, idx) => {
    const existent = roomOptions.facilities.find((elem) => elem.id === facility.facilityId);
    return (
      <span key={`faclitiy-${existent.id}`}>
        {existent.name}
        {idx < facilities.length - 1 ? ', ' : ''}
      </span>
    );
  });

const RoomDetailsModal = (props) => {
  const { t } = useTranslation();
  const { room, roomOptions, eventDetails } = props;

  return (
    <ModalInfo
      className="modal-room-details"
      modalIsOpen={props.modalOpen}
      closeModal={() => props.toggleModal()}
      onSubmit={() => props.toggleModal()}
      saveLabel={t('common.labels.close')}
      headerTitle={t('staffAndRoomsManagement.roomDetailsTitle')}>
      {!isEmpty(room) ? (
        <section>
          <div>
            <h4>
              {room.name} ({room.capacity} {t('common.labels.seats')})
            </h4>
            <div className="type-timing-labels">
              <div className="label-box-radius">
                {room.roomType ? room.roomType.name : t('testCenter.roomDashboard.noTypeLabel')}
              </div>
              {room.timing && (
                <div className="label-box-radius">{getRoomTimingName(room.timing, props.programCode, props.mappedRoomTiming)}</div>
              )}
              {props.room.singleRoomFlag && (
                <div className="label-box-radius">{t('rosters.singleRoom.label')}</div>
              )}
              {room.roomType && (
                <span className="label-box-radius">
                  {room.roomType.onlineFlag ? t('common.labels.online') : t('common.labels.paper')}
                </span>
              )}
            </div>
            <EventDetails
              {...eventDetails}
              locationName={room.location && room.location.name}
              locationArea={room.location && room.location.area}
            />
          </div>
          <div className="room-info-section">
            <p>
              <span className="fw-bold">{t('roomBuilder.roomShapeLabel')}</span>
              <span>{room.shape?.name}</span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.facilitiesLabel')}</span>
              <span>
                {room.roomFacilities.length
                  ? mapFacilities(room.roomFacilities, roomOptions)
                  : t('common.labels.none')}
              </span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.seatingLabel')}</span>
              <span>{room.seating?.name}</span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.deskTypesLabel')}</span>
              <span>{room.deskType?.name}</span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.supervisorDeskLocationsLabel')}</span>
              <span>{room.supervisorDesk?.name}</span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.nrOfSeatsLabel')}</span>
              <span>
                {room.seatingRowCount || room.seatingColumnCount
                  ? t('roomBuilder.rowsOfLabel', {
                      rowsNumber: room.seatingRowCount || '-',
                      colNumber: room.seatingColumnCount || '-',
                    })
                  : t('common.labels.notSpecified')}
              </span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.writingSurfacesLabel')}</span>
              <span>
                {room.writingSurfaceWidth || room.writingSurfaceDepth
                  ? t('roomBuilder.writingSurfaceInchLabel', {
                      width: room.writingSurfaceWidth || '-',
                      depth: room.writingSurfaceDepth || '-',
                    })
                  : t('common.labels.notSpecified')}
              </span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.shoulderToShoulderLabel')}</span>
              <span>
                {room.distanceBetweenShoulders
                  ? `${room.distanceBetweenShoulders} ${t('common.labels.feet')}`
                  : t('common.labels.notSpecified')}
              </span>
            </p>
            <p>
              <span className="fw-bold">{t('roomBuilder.headToHeadLabel')}</span>
              <span>
                {room.distanceBetweenHeads
                  ? `${room.distanceBetweenHeads} ${t('common.labels.feet')}`
                  : t('common.labels.notSpecified')}
              </span>
            </p>
          </div>
          <div className="">
            <button
              className="pl-0 btn-link text-pacific-blue fw-bold"
              onClick={() => props.onEditRoom(room.id)}>
              <span>
                <PencilIcon />
                {t('roomBuilder.editInformationLabel')}
              </span>
            </button>
            <button
              className="btn-link remove-button fw-bold"
              onClick={() => props.onDeleteRoom(false, true)}
              disabled={props.deleteDisabled}>
              <span>
                <GarbageIcon />
                {t('roomBuilder.deleteRoomLabel')}
              </span>
            </button>
          </div>
        </section>
      ) : null}
    </ModalInfo>
  );
};

export default RoomDetailsModal;

RoomDetailsModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  room: PropTypes.shape({}).isRequired,
  mappedRoomTiming: PropTypes.shape({}).isRequired,
  roomOptions: PropTypes.shape({
    facilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  eventDetails: PropTypes.shape({}).isRequired,
  onEditRoom: PropTypes.func.isRequired,
  onDeleteRoom: PropTypes.func.isRequired,
  deleteDisabled: PropTypes.bool,
};
